import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, ShieldCheck, ShieldAlert, Loader2, DollarSign, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-white rounded-lg border border-[#D8E6F1] px-3 py-2.5 text-[#0C2A40] placeholder:text-[#93A5B5] focus:border-[#2880B8] focus:ring-2 focus:ring-[#2880B8]/20 focus:outline-none transition-all text-sm";

const statusStyle = {
  clean: "text-[#1F6EA1] bg-[#E7F1F8]",
  paid: "text-green-700 bg-green-50",
  denied: "text-red-600 bg-red-50",
  pending: "text-amber-700 bg-amber-50",
};

export default function RCM() {
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ patient_name: "", payer: "", cpt: "", icd10: "", amount: "" });

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/rcm/claims"), api.get("/rcm/summary")]);
    setClaims(c.data);
    setSummary(s.data);
  };

  useEffect(() => { load(); }, []);

  const createClaim = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rcm/claims", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Claim created");
      setAddOpen(false);
      setForm({ patient_name: "", payer: "", cpt: "", icd10: "", amount: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const action = async (id, kind) => {
    setBusyId(id + kind);
    try {
      const { data } = await api.post(`/rcm/claims/${id}/${kind}`);
      if (kind === "scrub") toast[data.status === "clean" ? "success" : "error"](data.status === "clean" ? "Claim passed scrub — clean" : `Denied: ${data.denial_reason}`);
      else toast.success("Payment posted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter text-[#0C2A40]">RCM Claims</h1>
          <p className="mt-2 text-[#51667A]">Create, scrub with the Denial Agent, and post payments.</p>
        </div>
        <button data-testid="rcm-add-claim" onClick={() => setAddOpen((o) => !o)} className="hidden md:flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#2880B8] text-white font-mono text-[10px] tracking-[0.15em] uppercase hover:bg-[#1F6EA1] transition-colors">
          <Plus size={14} /> New Claim
        </button>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Clean Rate", v: `${summary.clean_rate}%`, icon: ShieldCheck },
            { l: "Total Billed", v: `$${summary.total_amount.toLocaleString()}`, icon: DollarSign },
            { l: "Collected", v: `$${summary.collected.toLocaleString()}`, icon: DollarSign },
            { l: "Denied / Pending", v: `${summary.denied} / ${summary.pending}`, icon: Clock },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.l} data-testid={`rcm-kpi-${k.l.toLowerCase().split(" ")[0]}`} className="bg-white rounded-2xl border border-[#D8E6F1] p-5">
                <Icon size={18} className="text-[#2880B8]" />
                <div className="mt-3 font-mono text-2xl md:text-3xl font-bold text-[#0C2A40]">{k.v}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.12em] uppercase text-[#93A5B5]">{k.l}</div>
              </div>
            );
          })}
        </div>
      )}

      {addOpen && (
        <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} onSubmit={createClaim} className="mt-6 bg-white rounded-2xl border border-[#D8E6F1] p-5 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <input data-testid="claim-patient" required placeholder="Patient name" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className={`${inputCls} md:col-span-2`} />
          <input data-testid="claim-payer" required placeholder="Payer" value={form.payer} onChange={(e) => setForm({ ...form, payer: e.target.value })} className={inputCls} />
          <input data-testid="claim-cpt" required placeholder="CPT (e.g. 99213)" value={form.cpt} onChange={(e) => setForm({ ...form, cpt: e.target.value })} className={inputCls} />
          <input data-testid="claim-icd" required placeholder="ICD-10 (e.g. E11.9)" value={form.icd10} onChange={(e) => setForm({ ...form, icd10: e.target.value })} className={inputCls} />
          <div className="flex gap-2">
            <input data-testid="claim-amount" required type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
            <button data-testid="save-claim" type="submit" className="px-4 py-2.5 rounded-lg bg-[#0C2A40] text-white font-mono text-[10px] tracking-[0.15em] uppercase shrink-0">Add</button>
          </div>
        </motion.form>
      )}

      {/* Claims table */}
      <div className="mt-8 bg-white rounded-2xl border border-[#D8E6F1] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#D8E6F1] bg-[#F2F8FC] text-left">
                {["Claim #", "Patient", "Payer", "CPT", "ICD-10", "Amount", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] tracking-[0.12em] uppercase text-[#93A5B5] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.id} data-testid={`claim-row-${c.id}`} className="border-b border-[#F2F8FC] hover:bg-[#F2F8FC]/60">
                  <td className="px-4 py-3 font-mono text-xs text-[#0C2A40] whitespace-nowrap">{c.claim_no}</td>
                  <td className="px-4 py-3 text-[#0C2A40] whitespace-nowrap">{c.patient_name}</td>
                  <td className="px-4 py-3 text-[#51667A] whitespace-nowrap">{c.payer}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#51667A]">{c.cpt}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#51667A]">{c.icd10}</td>
                  <td className="px-4 py-3 font-mono text-[#0C2A40] whitespace-nowrap">${c.amount.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] tracking-[0.1em] uppercase px-2 py-1 rounded-full ${statusStyle[c.status]}`} title={c.denial_reason || ""}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-2">
                      {c.status === "pending" && (
                        <button data-testid={`scrub-${c.id}`} onClick={() => action(c.id, "scrub")} disabled={busyId === c.id + "scrub"} className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#2880B8] text-[#2880B8] font-mono text-[9px] tracking-[0.12em] uppercase hover:bg-[#2880B8] hover:text-white transition-colors">
                          {busyId === c.id + "scrub" ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />} Scrub
                        </button>
                      )}
                      {c.status === "clean" && (
                        <button data-testid={`pay-${c.id}`} onClick={() => action(c.id, "pay")} disabled={busyId === c.id + "pay"} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#2880B8] text-white font-mono text-[9px] tracking-[0.12em] uppercase hover:bg-[#1F6EA1] transition-colors">
                          {busyId === c.id + "pay" ? <Loader2 size={12} className="animate-spin" /> : <DollarSign size={12} />} Post Payment
                        </button>
                      )}
                      {c.status === "denied" && <span className="font-mono text-[9px] text-red-500 uppercase tracking-[0.1em] max-w-[200px] truncate">{c.denial_reason}</span>}
                      {c.status === "paid" && <span className="font-mono text-[9px] text-green-600 uppercase tracking-[0.1em]">Settled</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
